import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { extractErrorMessage } from '../../api/errors';
import { AffiliateDraft, useCreateAffiliate, useUpdateAffiliate } from '../../queries/affiliates';

interface Props {
  affiliate?: AffiliateDraft; // present = edit
  onClose: () => void;
}

const inputClass = 'mt-1 w-full rounded-md border border-input bg-background px-2 py-2 text-sm';

export function AffiliateFormDialog({ affiliate, onClose }: Props) {
  const isEdit = !!affiliate?.id;
  const create = useCreateAffiliate();
  const update = useUpdateAffiliate();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<AffiliateDraft>({
    affiliate_code: affiliate?.affiliate_code ?? '',
    display_name: affiliate?.display_name ?? '',
    commission_type: affiliate?.commission_type ?? 'percentage',
    commission_value: affiliate?.commission_value ?? 20,
    payment_schedule: affiliate?.payment_schedule ?? 'recurring',
    payment_limit: affiliate?.payment_limit ?? null,
    status: affiliate?.status ?? 'active',
    notes: affiliate?.notes ?? '',
    stripe_account_id: affiliate?.stripe_account_id ?? '',
  });

  const set = (patch: Partial<AffiliateDraft>) => setForm((f) => ({ ...f, ...patch }));
  const saving = create.isPending || update.isPending;

  const handleSave = () => {
    if (!form.commission_value || Number(form.commission_value) <= 0) {
      setError('Commission value must be greater than 0.');
      return;
    }
    if (form.payment_schedule === 'payment_limited' && !(Number(form.payment_limit) > 0)) {
      setError('Payment limit must be greater than 0 for the payment-limited schedule.');
      return;
    }
    setError(null);
    const draft: AffiliateDraft = { ...form, commission_value: Number(form.commission_value) };
    const onError = (err: unknown) => setError(extractErrorMessage(err, 'Failed to save affiliate.'));
    if (isEdit && affiliate?.id) {
      update.mutate({ id: affiliate.id, draft }, { onSuccess: onClose, onError });
    } else {
      create.mutate(draft, { onSuccess: onClose, onError });
    }
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[90vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-border bg-background p-6 shadow-lg">
          <Dialog.Title className="text-lg font-semibold">{isEdit ? 'Edit affiliate' : 'New affiliate'}</Dialog.Title>
          <p className="mt-1 text-sm text-muted-foreground">Metadata only. Stripe-Connect onboarding is a separate step.</p>

          <div className="mt-4 grid grid-cols-2 gap-3">
            <label className="block text-sm">
              Display name
              <input className={inputClass} value={form.display_name ?? ''} onChange={(e) => set({ display_name: e.target.value })} />
            </label>
            <label className="block text-sm">
              Affiliate code {!isEdit && <span className="text-muted-foreground">(auto if blank)</span>}
              <input className={inputClass} value={form.affiliate_code ?? ''} onChange={(e) => set({ affiliate_code: e.target.value })} />
            </label>
            <label className="block text-sm">
              Commission type
              <select className={inputClass} value={form.commission_type} onChange={(e) => set({ commission_type: e.target.value })}>
                <option value="percentage">Percentage</option>
                <option value="flat_rate">Flat rate</option>
              </select>
            </label>
            <label className="block text-sm">
              Commission value {form.commission_type === 'percentage' ? '(%)' : '($)'}
              <input type="number" min={0} step="0.01" className={inputClass} value={String(form.commission_value ?? '')} onChange={(e) => set({ commission_value: e.target.value })} />
            </label>
            <label className="block text-sm">
              Payment schedule
              <select className={inputClass} value={form.payment_schedule} onChange={(e) => set({ payment_schedule: e.target.value })}>
                <option value="recurring">Recurring</option>
                <option value="once_off">Once off</option>
                <option value="payment_limited">Payment limited</option>
              </select>
            </label>
            {form.payment_schedule === 'payment_limited' && (
              <label className="block text-sm">
                Payment limit
                <input type="number" min={1} className={inputClass} value={String(form.payment_limit ?? '')} onChange={(e) => set({ payment_limit: Number(e.target.value) })} />
              </label>
            )}
            <label className="block text-sm">
              Status
              <select className={inputClass} value={form.status} onChange={(e) => set({ status: e.target.value })}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
              </select>
            </label>
            <label className="block text-sm">
              Stripe account id
              <input className={inputClass} value={form.stripe_account_id ?? ''} onChange={(e) => set({ stripe_account_id: e.target.value })} placeholder="acct_…" />
            </label>
            <label className="col-span-2 block text-sm">
              Notes
              <textarea className={`${inputClass} min-h-[60px]`} value={form.notes ?? ''} onChange={(e) => set({ notes: e.target.value })} />
            </label>

            {error && <p className="col-span-2 text-sm text-destructive">{error}</p>}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-md border border-input px-3 py-1.5 text-sm font-medium hover:bg-accent">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
